import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import "./SearchModal.css";

export default function SearchModal({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const history = useHistory();

  const searchablePages = [
    { title: "Home", path: "/", category: "Navigation" },
    { title: "About Us", path: "/about", category: "Navigation" },
    { title: "People", path: "/people", category: "Navigation" },
    { title: "Contact", path: "/contact", category: "Navigation" },
    { title: "Momentum", path: "/approaches?name=0", category: "Investment Approaches" },
    { title: "Total Return", path: "/approaches?name=1", category: "Investment Approaches" },
    { title: "Multi-Asset", path: "/approaches?name=2", category: "Investment Approaches" },
    { title: "Momentum Enhancer", path: "/approaches?name=3", category: "Investment Approaches" },
    { title: "Total Return Enhancer", path: "/approaches?name=4", category: "Investment Approaches" },
    { title: "Multi-Asset Enhancer", path: "/approaches?name=5", category: "Investment Approaches" },
    { title: "Portfolio Return Optimiser", path: "/approaches?name=6", category: "Investment Approaches" },
    { title: "Fee Model", path: "/fee-illustration", category: "Navigation" },
    { title: "Investment Approaches", path: "/approaches", category: "Navigation" },
    { title: "Invest Directly", path: "/onboarding", category: "External", isExternal: true },
    { title: "Latest Disclosure Document", path: "/files/Disclosure_Document.pdf", category: "Disclosures", isPdf: true },
    { title: "Our Investor Charter", path: "/files/Agreya Investor charter.pdf", category: "Disclosures", isPdf: true },
    { title: "Data on complaints", path: "/files/data-on-complaints.pdf", category: "Disclosures", isPdf: true },
    { title: "ICICI Bank Custody", path: "https://onlinefa.icici.bank.in/wealthspectrum/portal/sign-in", category: "Login", isExternal: true },
    { title: "Nuvama Custody", path: "https://eclientreporting.nuvamaassetservices.com/wealthspectrum/app/loginWith", category: "Login", isExternal: true },
  ];

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setResults([]);
      setSelectedIndex(-1);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = searchablePages.filter((page) =>
      page.title.toLowerCase().includes(query) ||
      page.category.toLowerCase().includes(query)
    );

    setResults(filtered);
    setSelectedIndex(-1);
  }, [searchQuery]);

  const handleNavigate = (item) => {
    if (item.isExternal) {
      window.open(item.path, "_blank", "noopener,noreferrer");
    } else if (item.isPdf) {
      window.open(item.path, "_blank");
    } else {
      history.push(item.path);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < results.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleNavigate(results[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="search-modal-overlay" onClick={onClose} aria-hidden="true" role="presentation"></div>
      <div
        className="search-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
        aria-describedby="search-modal-description"
      >
        <div className="search-modal-header">
          <h2 id="search-modal-title" className="sr-only">
            Search Pages and Features
          </h2>
          <p id="search-modal-description" className="sr-only">
            Search through all available pages and features. Use arrow keys to navigate results, Enter to select.
          </p>
          <div className="search-modal-input-wrapper">
            <FiSearch className="search-modal-icon" aria-hidden="true" />
            <input
              id="search-modal-input"
              ref={searchInputRef}
              type="text"
              placeholder="Search pages, features, or help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Search for pages and features"
              aria-expanded={results.length > 0}
              aria-controls="search-results-list"
              aria-describedby="search-modal-description"
              autoComplete="off"
            />
          </div>
          <button
            className="search-modal-close"
            onClick={onClose}
            aria-label="Close search"
            title="Close search (Esc)"
          >
            <FiX />
          </button>
        </div>

        {results.length > 0 ? (
          <div className="search-modal-results">
            <ul
              id="search-results-list"
              className="search-results-list"
              role="listbox"
            >
              {results.map((result, index) => (
                <li
                  key={`${result.path}-${index}`}
                  className={`search-result-item ${
                    selectedIndex === index ? "selected" : ""
                  }`}
                  onClick={() => handleNavigate(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <div className="search-result-title">{result.title}</div>
                  <div className="search-result-category">{result.category}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : searchQuery.trim() !== "" ? (
          <div className="search-modal-no-results">
            <p>No results found for "{searchQuery}"</p>
            <p className="search-hint">
              Try searching for: Home, About, People, Contact, or any investment approach.
            </p>
          </div>
        ) : null}

        {/* <div className="search-modal-footer">
          <div className="search-tips">
            <span className="tip">
              <kbd>↑↓</kbd> Navigate
            </span>
            <span className="tip">
              <kbd>Enter</kbd> Select
            </span>
            <span className="tip">
              <kbd>Esc</kbd> Close
            </span>
          </div>
        </div> */}
      </div>
    </>
  );
}
